import React, { useState, useEffect, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Modal, Form, Input, Empty, List, Button, Popconfirm, message, Space } from 'antd';
import { debounce } from 'lodash';
import { roomApi } from '@/apis';
import HoverTool from '@/components/hover-tool';
import './index.less';

const defaultLabelCol = {
  labelCol: {
    flex: '80px',
    style: {
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-all',
      width: '100%',
    },
  },
  wrapperCol: {
    flex: 1,
    style: {
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-all',
      maxWidth: '286px',
    },
  },
};

const DailyAddErpModal = (props, ref) => {
  const {
    visible,
    setVisible,
    roomId,
    title = (
      <HoverTool
        text="新增人员"
        hover="日常活动管理组成员，可协助组长共同管理日常活动，包括在资源位管理下新建和管理资源等"
        style={{ cursor: 'pointer' }}
      />
    ),
    extraTitle,
    extraDis,
    inputTitle = '添加成员',
    listTitle = (len) => `成员管理(${len})`,
    footer = null,
  } = props;

  const [modalForm] = Form.useForm();
  const [inlineErpList, setInlineErpList] = useState([]);

  const memoizedFetchErpList = useCallback(async () => {
    let didMount = true;
    if (visible && roomId) {
      const { ret, msg, data } = await roomApi.queryRoomMembers({ roomId });
      if (ret && ret === '0') {
        if (!didMount) return;
        setInlineErpList(data);
      } else {
        message.error(msg || '网络异常请稍后重试');
      }
    }
    return () => {
      didMount = false;
    };
  }, [visible, roomId]);

  /* 初始化获取erplist */
  useEffect(() => {
    memoizedFetchErpList();
  }, [memoizedFetchErpList]);

  /* ************************* 回抛事件 ************************ */
  /* 提交表单，添加erp，需要先校验 */
  async function onSubmit(values) {
    // 默认form的值
    const postErps = values?.erps?.replace(/，/gi, ',');
    const { ret, msg } = await roomApi.addRoomMembers({ erps: postErps, roomId });
    if (ret === '0') {
      message.success('添加成功');
      modalForm && modalForm.resetFields();
      memoizedFetchErpList();
    } else {
      message.error(msg || '添加失败,网络异常请稍后');
    }
  }

  /* 删除回调 */
  async function handleInlineDeleteItem(item) {
    const { ret, msg, data } = await roomApi.removeMember({ erp: item?.erp, roomId });
    if (ret === '0') {
      message.success('删除成功');
      memoizedFetchErpList();
    } else {
      message.error(msg || '网络异常请稍后');
    }
  }

  /* 转移组长权限 */
  async function handleTranstlateOwner(item) {
    const { ret, msg, data } = await roomApi.transferLeader({ erp: item?.erp, roomId });
    if (ret === '0') {
      message.success('权限转移成功');
      memoizedFetchErpList();
    } else {
      message.error(msg || '网络异常请稍后');
    }
  }

  /* cancel回调 */
  function handleInlineCancel(e) {
    modalForm && modalForm.resetFields();
    setInlineErpList([]);
    setVisible(false);
  }

  /* render-props, 当外围不传的时候走内部的 */
  const renderErpList = InlineRenderErpList;
  function InlineRenderErpList() {
    if (inlineErpList && Array.isArray(inlineErpList) && inlineErpList.length > 0) {
      return (
        <List
          className="erp-loadmore-list"
          itemLayout="horizontal"
          dataSource={inlineErpList}
          renderItem={(item) => (
            <List.Item
              className="erp-loadmore-list-item"
              extra={
                <Space size={0} direction="vertical" align="end">
                  {item?.canDelete === 1 && (
                    <Popconfirm
                      title="确认删除？"
                      onConfirm={debounce(() => handleInlineDeleteItem(item), 200)}
                      key="list-item-delete"
                      okButtonProps={{ shape: 'round' }}
                      cancelButtonProps={{ shape: 'round' }}
                    >
                      <Button type="link" size={'small'}>
                        删除
                      </Button>
                    </Popconfirm>
                  )}
                  {item?.canTransfer === 1 && (
                    <Popconfirm
                      title="确认转移组长权限？"
                      onConfirm={debounce(() => handleTranstlateOwner(item), 200)}
                      key="list-item-delete"
                      okButtonProps={{ shape: 'round' }}
                      cancelButtonProps={{ shape: 'round' }}
                    >
                      <Button type="link" size={'small'}>
                        转移组长权限
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              }
            >
              {item?.erp && item?.realName ? (
                <div className="user-info">
                  <div className="user-name">{item?.realName}</div>
                  <div className="user-erp">{item?.erp}</div>
                </div>
              ) : (
                '-'
              )}
            </List.Item>
          )}
        />
      );
    }

    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={'请先添加erp'} />;
  }

  return (
    <Modal
      okText="确认"
      cancelText="取消"
      className="daily-list-add-erp-modal"
      width={800}
      bodyStyle={{ paddingTop: '16px', paddingBottom: '16px', maxHeight: '622px', overflow: 'hidden auto' }}
      footer={footer}
      visible={visible}
      title={title}
      onCancel={debounce(handleInlineCancel, 200)}
    >
      {/* 拓展标题区 */}
      {extraTitle || extraDis ? (
        <div className="daily-common-extra-content-fa">
          <div className="extra-title-fa">{extraTitle}</div>
          <div className="extra-extraDis-fa">{extraDis}</div>
        </div>
      ) : null}
      <div className="daily-list-add-erp-fa">
        {/* 左侧form区域 */}
        <div className="erp-form-fa">
          <h3 className="erp-form-title">{inputTitle}</h3>
          <div className="erp-default-form-fa">
            <Form onFinish={onSubmit} form={modalForm} name="defaultForm" {...defaultLabelCol}>
              <Form.Item
                name="erps"
                label={
                  <span>
                    <span style={{ color: '#ff4d4f', marginRight: '2px' }}>*</span>
                    erp账号
                  </span>
                }
                style={{ marginBottom: '0px' }}
                rules={[
                  {
                    // 2、重复性校验, 2、以下erp已存在：XXX，XXX，请重新输入！
                    // 1、erp有效性校验, 1、以下erp无效：XXX，XXX，请重新输入
                    validator: (_, value) => {
                      if (!value) return;
                      return new Promise((resolve, reject) => {
                        // 找出无效的erp
                        roomApi
                          .checkMembers({
                            erps: value,
                            roomId: roomId,
                          })
                          .then((res) => {
                            const { ret } = res;
                            if (ret === '0' && res.data) {
                              if (
                                res?.data?.duplErps &&
                                Array.isArray(res?.data?.duplErp) &&
                                res?.data?.duplErp?.length > 0
                              ) {
                                const { duplErps } = res.data;
                                return reject(new Error(`以下erp已存在：${duplErps.join(',')}，请重新输入`));
                              } else if (res.data.invalidErps && res.data.invalidErps.length) {
                                const { invalidErps } = res.data;
                                return reject(new Error(`以下erp无效：${invalidErps.join(',')}，请重新输入`));
                              } else {
                                return resolve();
                              }
                            } else {
                              return reject(new Error(res?.msg || '网络异常请稍后'));
                            }
                          })
                          .finally(() => {
                            return resolve();
                          });
                      });
                    },
                  },
                ]}
              >
                <Input.TextArea placeholder="输入多个erp账号请以英文逗号区分" style={{ height: 98 }} />
              </Form.Item>
            </Form>
          </div>
          <div className="erp-extra-form-submit-btn">
            <Button type="primary" shape="round" onClick={debounce(() => modalForm.submit(), 200)}>
              添加
            </Button>
          </div>
        </div>
        {/* 右侧list区域 */}
        <div className="erp-list-fa">
          <h3 className="erp-list-title">
            {listTitle(
              inlineErpList && Array.isArray(inlineErpList) && inlineErpList.length > 0 ? inlineErpList.length : '0'
            )}
          </h3>
          <>{renderErpList()}</>
        </div>
      </div>
    </Modal>
  );
};

export default forwardRef(DailyAddErpModal);
